import { useState } from "react";
import { Button } from "../../components/Button";
import { ApiError, api } from "../../lib/api";
import type { WalletLedgerReason, WalletResponse } from "../../lib/api";
import { dollars } from "../Shopping/Shopping";
import styles from "./AccountSettings.module.css";

const errorText = (e: unknown, fallback: string): string =>
  e instanceof ApiError ? (e.body?.error ?? e.message) : fallback;

const dateLabel = (iso: string): string => new Date(iso).toLocaleDateString();

const REASON_LABELS: Record<WalletLedgerReason, string> = {
  bounty: "Referral bounty",
  payout: "Payout",
  partner_share: "Partner share",
  partner_clawback: "Clawback",
};

const signedDollars = (cents: number): string =>
  cents < 0 ? `−${dollars(-cents)}` : dollars(cents);

type WalletTabProps = {
  wallet: WalletResponse;
  onChanged: (wallet: WalletResponse) => void;
};

/** The Account-page Wallet tab (PRD PAYWALL §6, item 7): balance + ledger with running
 * balances, the affiliate link when an `affiliates` row exists, and "Transfer to bank" —
 * Stripe Connect express onboarding on first use, then one transfer of the full balance.
 * Where Connect is unavailable (sandbox), the payout POST answers with an honest 503
 * that renders verbatim below the button. */
export const WalletTab = ({ wallet, onChanged }: WalletTabProps) => {
  const [busy, setBusy] = useState(false);
  const [paidAmount, setPaidAmount] = useState<number | null>(null);
  const [payoutError, setPayoutError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const affiliateLink = wallet.affiliate
    ? `${window.location.origin}/register?ref=${wallet.affiliate.code}`
    : null;

  const handleCopy = async (): Promise<void> => {
    if (!affiliateLink) return;
    try {
      await navigator.clipboard.writeText(affiliateLink);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const handlePayout = async (): Promise<void> => {
    setBusy(true);
    setPayoutError(null);
    setPaidAmount(null);
    try {
      const result = await api.requestWalletPayout();
      if (result.status === "onboarding" && result.onboarding_url) {
        // Stripe Connect express onboarding (KYC + bank account) happens at Stripe;
        // the return URL lands back on this tab to click Transfer again.
        window.location.assign(result.onboarding_url);
        return;
      }
      setPaidAmount(result.amount_cents ?? 0);
      onChanged(await api.getWallet());
    } catch (e) {
      setPayoutError(errorText(e, "Couldn't start the transfer."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className={styles.panel}>
        <span className={styles.kicker}>Account wallet</span>
        <div className={styles.form}>
          <span className={styles.help}>
            Balance: <strong>{signedDollars(wallet.balance_cents)}</strong>
          </span>
          <span className={styles.help}>
            Transfers to your bank unlock at {dollars(wallet.payout_threshold_cents)}.
          </span>
          <div className={styles.actions}>
            <Button
              type="button"
              disabled={busy || !wallet.payout_eligible}
              onClick={() => void handlePayout()}
            >
              {busy ? "Working…" : "Transfer to bank"}
            </Button>
          </div>
          {paidAmount !== null && (
            <p className={styles.ok}>{dollars(paidAmount)} is on its way to your bank account.</p>
          )}
          {payoutError && (
            <div role="alert" className={styles.error}>
              {payoutError}
            </div>
          )}
        </div>
      </section>

      {wallet.affiliate && affiliateLink && (
        <section className={styles.panel}>
          <span className={styles.kicker}>Your referral link</span>
          <p className={styles.help}>
            Share this link — when an account that registered through it pays its first invoice,
            your wallet is credited {dollars(wallet.affiliate.bounty_cents)}.
          </p>
          <div className={styles.actions}>
            <code className={styles.code}>{affiliateLink}</code>
            <Button type="button" variant="outline" onClick={() => void handleCopy()}>
              {copied ? "Copied" : "Copy link"}
            </Button>
          </div>
        </section>
      )}

      <section className={styles.panel}>
        <span className={styles.kicker}>Activity</span>
        {wallet.ledger.length === 0 && <p className={styles.help}>No wallet activity yet.</p>}
        {wallet.ledger.length > 0 && (
          <table className={styles.statTable}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Reason</th>
                <th>Amount</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {wallet.ledger.map((row) => (
                <tr key={row.id}>
                  <td>{dateLabel(row.created_at)}</td>
                  <td>{REASON_LABELS[row.reason]}</td>
                  <td>{signedDollars(row.delta_cents)}</td>
                  <td>{signedDollars(row.running_balance_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
};
